'use client'

import React, { createContext, useContext, useState, type ReactNode } from 'react'

interface ModalContextType {
  isModalOpen: boolean
  modalType: 'project' | 'consultation' | 'service'
  openProjectModal: () => void
  openConsultationModal: () => void
  openServiceModal: () => void
  closeModal: () => void
  isSuccessModalOpen: boolean
  openSuccessModal: () => void
  closeSuccessModal: () => void
}

const ModalContext = createContext<ModalContextType | undefined>(undefined)

export const useModal = () => {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider')
  }
  return context
}

interface ModalProviderProps {
  children: ReactNode
}

export const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalType, setModalType] = useState<'project' | 'consultation' | 'service'>('project')
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)

  const openProjectModal = () => {
    setModalType('project')
    setIsModalOpen(true)
  }

  const openConsultationModal = () => {
    setModalType('consultation')
    setIsModalOpen(true)
  }

  const openServiceModal = () => {
    setModalType('service')
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    // Разблокируем скролл, если SuccessModal не открыт (проверяем через атрибут)
    setTimeout(() => {
      if (!document.body.hasAttribute('data-success-modal-open')) {
        document.body.style.overflow = ''
      }
    }, 100) // После анимации закрытия модалки
  }

  const openSuccessModal = () => {
    setIsSuccessModalOpen(true)
    // Блокируем скролл для SuccessModal
    document.body.style.overflow = 'hidden'
    // Добавляем атрибут для отслеживания
    document.body.setAttribute('data-success-modal-open', 'true')
  }

  const closeSuccessModal = () => {
    setIsSuccessModalOpen(false)
    // Разблокируем скролл после закрытия SuccessModal
    document.body.style.overflow = ''
    // Удаляем атрибут
    document.body.removeAttribute('data-success-modal-open')
  }

  return (
    <ModalContext.Provider value={{
      isModalOpen,
      modalType,
      openProjectModal,
      openConsultationModal,
      openServiceModal,
      closeModal,
      isSuccessModalOpen,
      openSuccessModal,
      closeSuccessModal
    }}>
      {children}
    </ModalContext.Provider>
  )
}
